import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type MouseEventHandler,
} from 'react';

/**
 * Which way a drag grows the column.
 * `right` is a handle on the column's right edge. `left` is a handle on the
 * left edge of a right-hand panel.
 */
type ResizeGrowDirection = 'right' | 'left';

type CreateResizeDragHookOptions = {
  /**
   * `localStorage` key used to persist the width. Also serves as the logical
   * identity of this resizer - every call to the returned hooks shares the
   * same state.
   */
  storageKey: string;
  /** Minimum width in pixels the column is allowed to shrink to. */
  min: number;
  /**
   * Maximum width as a fraction of `window.innerWidth` (0..1).
   * e.g. `0.6` caps the column at 60% of the viewport.
   */
  maxRatio: number;
  /** Initial width used when `localStorage` has no value for `storageKey`. */
  defaultWidth: number;
  /**
   * Which horizontal drag increases the width. Defaults to `right`, matching
   * a handle on the trailing edge of a left column.
   */
  growDirection?: ResizeGrowDirection;
  /**
   * Widths below this snap to `min` in one step, including a value restored
   * from `localStorage`. {@link ResizeDragHook.useExpandWidth} restores the
   * last width released at or above this threshold, including after a reload
   * while collapsed. Widths passed through while dragging closed are not
   * remembered.
   */
  collapseBelow?: number;
};

type ResizeDragState = {
  /** Current width in pixels, reactive to drags and external updates. */
  width: number;
  /** Attach to the drag handle's `onMouseDown`. */
  onMouseDown: MouseEventHandler;
};

export type ResizeDragHook = {
  /**
   * React hook: subscribe to width **and** return a drag handle.
   *
   * Prefer the narrower {@link ResizeDragHook.useResizeWidth} /
   * {@link ResizeDragHook.useResizeHandle} when a component only needs one
   * of the two, so thousands of rows don't all re-render on every drag
   * mousemove.
   */
  useResizeDrag: () => ResizeDragState;
  /**
   * React hook: subscribe to width only. Use this at a single ancestor to
   * publish the width as a CSS variable and let descendants read it from
   * CSS instead of subscribing individually.
   */
  useResizeWidth: () => number;
  /**
   * React hook: return a drag handle that does **not** subscribe to width.
   * Safe to attach in every row without causing N re-renders per drag frame.
   */
  useResizeHandle: () => MouseEventHandler;
  /**
   * Restore the last width at or above {@link CreateResizeDragHookOptions.collapseBelow}.
   * No-op when that option is unset.
   */
  useExpandWidth: () => () => void;
  /** Collapse to min while remembering the last open width. */
  useCollapseWidth: () => () => void;
};

/**
 * Build an isolated drag-to-resize hook bound to a specific `localStorage`
 * key. Each call creates an independent instance with its own state, its own
 * subscriber set, and its own persisted width.
 *
 * Hover styling (e.g. lighting up every handle when any one is hovered) is
 * intentionally **not** part of this hook - use CSS `:has()` against a
 * shared wrapper instead. It's cheaper and doesn't cause React re-renders.
 *
 * Typical usage is one factory call per feature, at module scope, re-exporting
 * the hook under a domain-meaningful name:
 *
 * @example
 * ```ts
 * // element-list-column-resize.hook.ts
 * export const {
 *   useResizeWidth: useElementListColumnWidth,
 *   useResizeHandle: useElementListColumnResizeHandle,
 * } = createResizeDragHook({
 *   storageKey: 'elementListLeftColumnWidth',
 *   min: 280,
 *   maxRatio: 0.6,
 *   defaultWidth: 320,
 * });
 * ```
 */
export const createResizeDragHook = ({
  storageKey,
  min,
  maxRatio,
  defaultWidth,
  growDirection = 'right',
  collapseBelow,
}: CreateResizeDragHookOptions): ResizeDragHook => {
  const widthListeners = new Set<() => void>();

  const openWidthKey = `${storageKey}Open`;

  const clamp = (value: number) => {
    const max = window.innerWidth * maxRatio;
    const clamped = Math.min(max, Math.max(min, value));
    if (collapseBelow !== undefined && clamped < collapseBelow) return min;
    return clamped;
  };

  const readStoredNumber = (key: string): number | undefined => {
    const raw = localStorage.getItem(key);
    if (raw === null) return;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  const readStored = (): number => readStoredNumber(storageKey) ?? defaultWidth;

  const readRememberedOpenWidth = (): number => {
    if (collapseBelow === undefined) return defaultWidth;
    const stored = readStoredNumber(openWidthKey);
    // A collapsed reload keeps the last open width in the sibling key.
    if (stored === undefined || stored < collapseBelow) return defaultWidth;
    return stored;
  };

  const storedWidth = readStored();
  let lastExpandedWidth =
    collapseBelow !== undefined && storedWidth >= collapseBelow
      ? storedWidth
      : readRememberedOpenWidth();

  // Lazy in-memory cache so `getWidth` never re-hits `localStorage` on render.
  let cachedWidth: number | undefined;
  const getWidth = () => {
    if (cachedWidth === undefined) cachedWidth = clamp(readStored());
    return cachedWidth;
  };

  const writeWidth = (next: number) => {
    const clamped = clamp(next);
    if (cachedWidth === clamped) return;
    cachedWidth = clamped;
    localStorage.setItem(storageKey, String(clamped));
    widthListeners.forEach((cb) => cb());
  };

  /** Width last released while open. A collapse drag must not shrink this. */
  const rememberExpandedWidth = () => {
    if (collapseBelow === undefined) return;
    const width = getWidth();
    if (width < collapseBelow) return;
    lastExpandedWidth = width;
    localStorage.setItem(openWidthKey, String(width));
  };

  const expandWidth = () => {
    if (collapseBelow === undefined) return;
    writeWidth(lastExpandedWidth);
  };

  const collapseWidth = () => {
    if (collapseBelow === undefined) return;
    rememberExpandedWidth();
    writeWidth(min);
  };

  const subscribeWidth = (cb: () => void) => {
    widthListeners.add(cb);
    return () => {
      widthListeners.delete(cb);
    };
  };

  const useResizeWidth = (): number =>
    useSyncExternalStore(subscribeWidth, getWidth);

  const useResizeHandle = (): MouseEventHandler => {
    // Track an in-flight drag so we can tear down listeners if the consuming
    // component unmounts mid-drag.
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(
      () => () => {
        cleanupRef.current?.();
        cleanupRef.current = null;
      },
      [],
    );

    return useCallback<MouseEventHandler>((e) => {
      if (e.button !== 0) return;
      cleanupRef.current?.();
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = getWidth();

      const prevUserSelect = document.body.style.userSelect;
      const prevCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';

      const onMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const signedDelta = growDirection === 'left' ? -delta : delta;
        writeWidth(startWidth + signedDelta);
      };

      const stop = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', stop, true);
        document.removeEventListener('pointerup', stop, true);
        document.removeEventListener('pointercancel', stop, true);
        window.removeEventListener('blur', stop);
        document.body.style.userSelect = prevUserSelect;
        document.body.style.cursor = prevCursor;
        cleanupRef.current = null;
        rememberExpandedWidth();
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', stop, true);
      document.addEventListener('pointerup', stop, true);
      document.addEventListener('pointercancel', stop, true);
      window.addEventListener('blur', stop);
      cleanupRef.current = stop;
    }, []);
  };

  const useExpandWidth = (): (() => void) => useCallback(expandWidth, []);

  const useCollapseWidth = (): (() => void) => useCallback(collapseWidth, []);

  const useResizeDrag = (): ResizeDragState => ({
    width: useResizeWidth(),
    onMouseDown: useResizeHandle(),
  });

  return {
    useResizeDrag,
    useResizeWidth,
    useResizeHandle,
    useExpandWidth,
    useCollapseWidth,
  };
};
